//
// useReputationScore — client hook for fetching + caching + refreshing
// wallet reputation scores from the DRS API (via our BFF).
//
// Cache layers (matches the QNS multi-layer approach):
//   1. localStorage      — 30 min, persists across reloads / tabs. Provides
//                          instant render on repeat views ("no flash of
//                          nothing" for a familiar address).
//   2. React Query in-mem — dedupes concurrent lookups (e.g. header badge +
//                          reputation tab both reading the same address).
//   3. HTTP Cache-Control — BFF sets max-age=1800 for hits, 30s for misses.
//   4. DRS server-side    — 30 day store; hits cheap and fast on their side.
//
// TTLs deliberately shorter than QNS because reputation is a MUTABLE score
// that changes with wallet activity, whereas domain ownership rarely
// changes. 30 min feels fresh without hammering upstreams.
//
// Feature flag: NEXT_PUBLIC_REPUTATION_ENABLED must be "true" or the hook
// no-ops entirely. Independent of the badge / tab flags.
//
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';

import { getEnvValue } from 'configs/app/utils';

export interface ReputationFactor {
  factor: string;
  label: string;
  description: string;
  source: string;
  weight: number;
  raw: Record<string, unknown>;
  normalised: number;
  points: number;
}

export type ReputationTier = 'New' | 'Active' | 'Trusted' | 'Strong' | 'Elite' | string;

export interface ReputationData {
  address: string;
  score: number;
  tier: ReputationTier;
  penalty: number;
  penaltyDescription: string;
  modelVersion: string;
  factors: Array<ReputationFactor>;
  authErrors: Array<string>;
  unavailableFactors: Array<string>;
  computedAt: string;
  lastRefreshAt: string;
  cached: boolean;
  source: string;
  persisted?: boolean;
  resolvedFrom?: { domain: string; owner: string };
}

interface ApiEnvelope {
  data: ReputationData | null; resolvedFrom?: { domain: string; owner: string };
}

const REPUTATION_ENABLED = getEnvValue('NEXT_PUBLIC_REPUTATION_ENABLED') !== 'false';

const LOCAL_STORAGE_KEY = 'reputation-cache-v1';
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 min
const MISS_TTL_MS = 2 * 60 * 1000; // 2 min for "unavailable" so brief outages recover fast

const ADDRESS_RE = /^0x[a-fA-F0-9]{40}$/;
const DOMAIN_RE = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]\.qie$/;

interface PersistedEntry {
  data: ReputationData | null;
  expiresAt: number;
}

let memoryCache: Record<string, PersistedEntry> = {};
let cacheLoaded = false;

function loadCache(): void {
  if (cacheLoaded || typeof window === 'undefined') return;
  cacheLoaded = true;
  try {
    const raw = window.localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, PersistedEntry>;
    const now = Date.now();
    memoryCache = Object.fromEntries(
      Object.entries(parsed).filter(([ , v ]) =>
        v && typeof v.expiresAt === 'number' && v.expiresAt > now,
      ),
    );
  } catch {
    memoryCache = {};
  }
}

function persistCache(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(memoryCache));
  } catch { /* quota / storage disabled */ }
}

function getCached(key: string): PersistedEntry | undefined {
  loadCache();
  const entry = memoryCache[key];
  if (!entry) return undefined;
  if (entry.expiresAt < Date.now()) {
    delete memoryCache[key];
    return undefined;
  }
  return entry;
}

function setCached(key: string, data: ReputationData | null): void {
  loadCache();
  const ttl = data ? CACHE_TTL_MS : MISS_TTL_MS;
  memoryCache[key] = { data, expiresAt: Date.now() + ttl };
  persistCache();
}

function invalidateCache(key: string): void {
  loadCache();
  delete memoryCache[key];
  persistCache();
}

export function isValidReputationInput(input: string | undefined | null): input is string {
  if (typeof input !== 'string') return false;
  const lower = input.trim().toLowerCase();
  if (ADDRESS_RE.test(lower)) return true;
  if (DOMAIN_RE.test(lower) && !lower.includes('--')) return true;
  return false;
}

function normalize(input: string): string {
  return input.trim().toLowerCase();
}

async function fetchReputation(key: string, signal?: AbortSignal): Promise<ReputationData | null> {
  try {
    const res = await fetch(`/api/reputation/${ encodeURIComponent(key) }/factors`, { signal });
    if (!res.ok) return null;
    const body = await res.json() as ApiEnvelope;
    return body.data ?? null;
  } catch {
    return null;
  }
}

async function queryFnWithPersistence(key: string, signal?: AbortSignal): Promise<ReputationData | null> {
  const data = await fetchReputation(key, signal);
  setCached(key, data);
  return data;
}

function buildInitialData(key: string): { data: ReputationData | null; updatedAt: number } | undefined {
  const cached = getCached(key);
  if (!cached) return undefined;
  const ttl = cached.data ? CACHE_TTL_MS : MISS_TTL_MS;
  return { data: cached.data, updatedAt: cached.expiresAt - ttl };
}

export interface UseReputationScoreResult {
  data: ReputationData | null;
  isPending: boolean;
  isRefreshing: boolean;
  refresh: () => void;
  // When refresh was denied by the server (rate limited); null otherwise.
  refreshError: { message: string; retryAfterSeconds?: number } | null;
  // Clear any transient refresh error so the button can be tried again later.
  clearRefreshError: () => void;
}

/**
 * Fetch and cache a wallet's reputation score.
 *
 * @param input - either a wallet address (0x-prefixed 40 hex) or a `.qie` domain name.
 * @param options - `enabled: false` short-circuits the hook entirely (never fires).
 *
 * Returns `data: null` in all failure modes — invalid input, DRS down,
 * unregistered domain, etc. — so consumers just render "unavailable"
 * without try/catch.
 */
export default function useReputationScore(
  input: string | undefined | null,
  options: { enabled?: boolean } = {},
): UseReputationScoreResult {
  const queryClient = useQueryClient();
  const [ refreshError, setRefreshError ] = React.useState<UseReputationScoreResult['refreshError']>(null);

  const enabled = REPUTATION_ENABLED && options.enabled !== false && isValidReputationInput(input);
  const key = enabled ? normalize(input as string) : '';
  const initial = enabled ? buildInitialData(key) : undefined;

  const query = useQuery<ReputationData | null>({
    queryKey: [ 'reputation', key ],
    queryFn: ({ signal }) => queryFnWithPersistence(key, signal),
    enabled,
    initialData: initial?.data,
    initialDataUpdatedAt: initial?.updatedAt,
    staleTime: CACHE_TTL_MS,
    gcTime: CACHE_TTL_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  const refreshMutation = useMutation({
    mutationFn: async(): Promise<ReputationData | null> => {
      const res = await fetch(`/api/reputation/${ encodeURIComponent(key) }/refresh`, { method: 'POST' });
      if (res.status === 429) {
        const body = await res.json().catch(() => ({})) as { error?: string; retryAfterSeconds?: number };
        throw Object.assign(new Error(body.error || 'Rate limited'), {
          retryAfterSeconds: body.retryAfterSeconds,
          isRateLimit: true,
        });
      }
      if (!res.ok) {
        throw new Error('Reputation service unavailable. Please try again shortly.');
      }
      const body = await res.json() as ApiEnvelope;
      return body.data ?? null;
    },
    onSuccess: (freshData) => {
      // Rewrite our caches so every mounted hook sees the new score.
      setCached(key, freshData);
      queryClient.setQueryData([ 'reputation', key ], freshData);
      setRefreshError(null);
    },
    onError: (err: unknown) => {
      const e = err as { message?: string; retryAfterSeconds?: number };
      setRefreshError({ message: e?.message || 'Refresh failed', retryAfterSeconds: e?.retryAfterSeconds });
    },
  });

  const refresh = React.useCallback(() => {
    if (!enabled || refreshMutation.isPending) return;
    // Optimistically drop the stored entry so if the request errors, the
    // next mount will re-fetch instead of showing stale data.
    invalidateCache(key);
    refreshMutation.mutate();
  }, [ enabled, key, refreshMutation ]);

  const clearRefreshError = React.useCallback(() => setRefreshError(null), []);

  return {
    data: query.data ?? null,
    isPending: enabled && query.isFetching && !query.data,
    isRefreshing: refreshMutation.isPending,
    refresh,
    refreshError,
    clearRefreshError,
  };
}